/**
 * The `/watch` screen's UI state (§12.4).
 *
 * `useFeedUiState`'s contract against `watch:main`: loaded **before the first #71 fetch** — the
 * query stays disabled until `ready` flips, so a persisted scope is never briefly ignored and page
 * one is never fetched twice — defaulted on `404`, and written back debounced (500 ms).
 *
 * Search is deliberately not in here: it is session-only, exactly as on `/saved`.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { UI_STATE_MAX_BYTES, WATCH_UI_STATE_KEY } from '@lw/shared';
import { getUiState, putUiState } from '../../api/uiState';
import { QK } from '../../api/queryKeys';
import { useDebouncedCallback, useLatest } from '../../lib/hooks';
import {
  DEFAULT_WATCH_STATE,
  sanitizeWatchState,
  toStoredWatchState,
  type WatchState,
} from './watchState';

const WRITE_DEBOUNCE_MS = 500;

export type WatchUiStateHandle = {
  /** `false` until the stored value (or its 404) has arrived — gate the feed query on this. */
  ready: boolean;
  state: WatchState;
  update: (patch: Partial<WatchState>) => void;
  /** Loading the state failed; the screen offers a retry rather than guessing. */
  error: unknown;
  retry: () => void;
};

export function useWatchUiState(): WatchUiStateHandle {
  const query = useQuery({
    queryKey: QK.uiState(WATCH_UI_STATE_KEY),
    queryFn: ({ signal }) => getUiState<unknown>(WATCH_UI_STATE_KEY, signal),
    staleTime: Number.POSITIVE_INFINITY,
    // A cached value from a previous visit is authoritative; refetching would race the writer.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const [state, setState] = useState<WatchState | null>(null);
  const seeded = useRef(false);
  const fellBack = useRef(false);

  // Seed once: from whatever the server had, from the defaults on 404, and — so a broken ui-state
  // endpoint can never brick the screen — from the defaults on failure too. A successful retry
  // still replaces the fallback with the stored value.
  useEffect(() => {
    if (query.isSuccess && !seeded.current) {
      seeded.current = true;
      fellBack.current = false;
      setState(sanitizeWatchState(query.data));
      return;
    }
    if (query.isError && !seeded.current && !fellBack.current) {
      fellBack.current = true;
      setState(sanitizeWatchState(null));
    }
  }, [query.isSuccess, query.isError, query.data]);

  const persist = useDebouncedCallback((next: WatchState) => {
    const payload = toStoredWatchState(next);
    // The endpoint caps the body at 16 KB (#32); this blob is orders of magnitude smaller, but a
    // pathological selection is still worth refusing locally rather than 413-ing.
    if (JSON.stringify(payload).length > UI_STATE_MAX_BYTES) return;
    void putUiState(WATCH_UI_STATE_KEY, payload).catch(() => {
      // Persistence is best-effort: the screen keeps working with in-memory state.
    });
  }, WRITE_DEBOUNCE_MS);

  // Driven from a ref rather than from inside the state updater: React skips updaters for an
  // unmounted component, and the last change before navigating away must still reach the server.
  const stateRef = useLatest(state);

  const update = useCallback(
    (patch: Partial<WatchState>) => {
      const next: WatchState = { ...(stateRef.current ?? DEFAULT_WATCH_STATE), ...patch };
      setState(next);
      persist(next);
    },
    [persist, stateRef],
  );

  return {
    ready: state !== null,
    state: state ?? DEFAULT_WATCH_STATE,
    update,
    error: query.isError ? query.error : null,
    retry: () => void query.refetch(),
  };
}
